import { createFileRoute, useNavigate, Navigate, Link } from "@tanstack/react-router";
import { Fragment, useState, useEffect } from "react";
import { ArrowLeft, PackageCheck, AlertTriangle } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { fetchWithAuth } from "@/lib/auth-client";
import type {
  PurchaseOrderDetailDto,
  CreateGRNRequest,
  GRNCondition,
} from "@procureflow/shared-types";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/grn/receive/$poId")({
  head: () => ({
    meta: [{ title: "Record Goods Receipt — ProcureFlow" }],
  }),
  component: GRNReceivePage,
});

const CONDITIONS: GRNCondition[] = ["Good", "Damaged", "Rejected"];

type LineDraft = {
  quantity_received: string;
  condition: GRNCondition | "";
  notes: string;
};

type LineError = Partial<Record<"quantity_received" | "condition", string>>;
type HeaderError = Partial<Record<"receiver_name" | "date_received", string>>;

function todayStr(): string {
  return new Date().toISOString().slice(0, 10);
}

function GRNReceivePage() {
  const { poId } = Route.useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [po, setPo] = useState<PurchaseOrderDetailDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadErrorStatus, setLoadErrorStatus] = useState<number | null>(null);

  const [receiverName, setReceiverName] = useState("");
  const [dateReceived, setDateReceived] = useState(todayStr());
  const [deliveryReference, setDeliveryReference] = useState("");
  const [lines, setLines] = useState<Record<string, LineDraft>>({});
  const [headerError, setHeaderError] = useState<HeaderError>({});
  const [lineErrors, setLineErrors] = useState<Record<string, LineError>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // Recording a receipt is a ProcurementOfficer / Admin action — the create
  // handler enforces the same; this only keeps the form off-limits to others.
  const permitted = user?.role === "ProcurementOfficer" || user?.role === "Admin";

  useEffect(() => {
    if (!permitted) return;
    let cancelled = false;

    async function fetchPO() {
      setLoading(true);
      setLoadError(null);
      setLoadErrorStatus(null);
      try {
        const res = await fetchWithAuth(`/api/purchase-orders/${poId}`);
        if (!res.ok) {
          const body = await res.json().catch(() => ({}));
          if (!cancelled) {
            setLoadErrorStatus(res.status);
            setLoadError(body.message ?? "Failed to load purchase order.");
          }
          return;
        }
        const { data } = (await res.json()) as { data: PurchaseOrderDetailDto };
        if (cancelled) return;
        setPo(data);
        setLines(
          Object.fromEntries(
            data.line_items.map((li) => [
              li.id,
              { quantity_received: "", condition: "", notes: "" } as LineDraft,
            ]),
          ),
        );
      } catch {
        if (!cancelled) setLoadError("An unexpected error occurred.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchPO();
    return () => {
      cancelled = true;
    };
  }, [poId, permitted]);

  if (!permitted) {
    return <Navigate to="/purchase-orders/$id" params={{ id: poId }} />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-6 w-6 rounded-full border-2 border-primary border-t-transparent animate-spin" />
      </div>
    );
  }

  if (loadError || !po) {
    return (
      <div className="space-y-4">
        <Link
          to="/purchase-orders"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Purchase Orders
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          {loadErrorStatus === 403 ? (
            <>
              <p className="text-sm font-medium text-foreground">Access denied</p>
              <p className="text-sm text-muted-foreground">
                You do not have permission to view this purchase order.
              </p>
            </>
          ) : loadErrorStatus === 404 ? (
            <>
              <p className="text-sm font-medium text-foreground">Not found</p>
              <p className="text-sm text-muted-foreground">
                This purchase order does not exist or has been removed.
              </p>
            </>
          ) : (
            <p className="text-sm text-destructive">{loadError}</p>
          )}
        </div>
      </div>
    );
  }

  // GRN recording is blocked for Draft (no confirmed order) and Cancelled
  // (terminal) POs — US-030. The PO-detail action is hidden in those states,
  // but a direct navigation still lands here, so guard it.
  if (po.status === "Draft" || po.status === "Cancelled") {
    return (
      <div className="space-y-4">
        <Link
          to="/purchase-orders/$id"
          params={{ id: po.id }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> {po.po_number}
        </Link>
        <div className="rounded-2xl bg-card border border-border shadow-elegant p-10 text-center space-y-2">
          <p className="text-sm font-medium text-foreground">Goods receipt cannot be recorded</p>
          <p className="text-sm text-muted-foreground">
            Purchase order {po.po_number} is {po.status === "Draft" ? "a draft" : "cancelled"}.
            Receipts can only be recorded against Sent, Partially Received, or Received orders.
          </p>
        </div>
      </div>
    );
  }

  const sortedLines = [...po.line_items].sort((a, b) => a.sort_order - b.sort_order);

  function updateLine<K extends keyof LineDraft>(lineId: string, key: K, value: LineDraft[K]) {
    setLines((prev) => ({ ...prev, [lineId]: { ...prev[lineId], [key]: value } }));
    setLineErrors((prev) => {
      if (!prev[lineId]) return prev;
      const next = { ...prev };
      const e = { ...next[lineId] };
      if (key === "quantity_received") delete e.quantity_received;
      if (key === "condition") delete e.condition;
      if (Object.keys(e).length === 0) delete next[lineId];
      else next[lineId] = e;
      return next;
    });
    setFormError(null);
  }

  function parsedQty(lineId: string): number {
    const raw = lines[lineId]?.quantity_received.trim() ?? "";
    if (raw === "") return 0;
    const n = Number(raw);
    return Number.isInteger(n) && n >= 0 ? n : 0;
  }

  function validate(): boolean {
    if (!po) return false;
    const hErr: HeaderError = {};
    if (!receiverName.trim()) hErr.receiver_name = "Receiver name is required.";
    if (!dateReceived) hErr.date_received = "Date received is required.";

    const lErrs: Record<string, LineError> = {};
    let totalReceived = 0;
    for (const li of po.line_items) {
      const d = lines[li.id];
      const e: LineError = {};
      const raw = d.quantity_received.trim();
      let qty = 0;
      if (raw !== "") {
        const n = Number(raw);
        if (!Number.isInteger(n) || n < 0) {
          e.quantity_received = "Enter a whole number of 0 or more.";
        } else {
          qty = n;
        }
      }
      if (qty > 0 && !d.condition) {
        e.condition = "Select a condition.";
      }
      if (Object.keys(e).length > 0) lErrs[li.id] = e;
      totalReceived += qty;
    }

    setHeaderError(hErr);
    setLineErrors(lErrs);

    if (Object.keys(hErr).length > 0 || Object.keys(lErrs).length > 0) return false;
    if (totalReceived === 0) {
      setFormError("Enter a received quantity greater than zero for at least one line.");
      return false;
    }
    return true;
  }

  async function handleSubmit() {
    if (!po || !validate()) return;

    if (import.meta.env.DEV) {
      const { mswReady } = await import("@/mocks/browser");
      await mswReady;
    }

    setSaving(true);
    setFormError(null);
    try {
      const body: CreateGRNRequest = {
        receiver_name: receiverName.trim(),
        date_received: dateReceived,
        delivery_reference: deliveryReference.trim() || undefined,
        line_items: po.line_items
          // Only lines actually received carry signal — a 0 qty is omitted.
          .filter((li) => parsedQty(li.id) > 0)
          .map((li) => ({
            po_line_item_id: li.id,
            quantity_received: parsedQty(li.id),
            condition: lines[li.id].condition as GRNCondition,
            notes: lines[li.id].notes.trim() || undefined,
          })),
      };

      const res = await fetchWithAuth(`/api/purchase-orders/${poId}/grns`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}));
        throw new Error(errBody.message ?? "Failed to record goods receipt.");
      }

      const { data } = await res.json();
      // One-shot signal: the GRN detail page confirms the receipt on arrival.
      sessionStorage.setItem("grn:justRecorded", data.id);
      navigate({ to: "/grn/$id", params: { id: data.id } });
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "An unexpected error occurred.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <Link
          to="/purchase-orders/$id"
          params={{ id: po.id }}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="h-4 w-4" /> {po.po_number}
        </Link>
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-[var(--success)]/10 grid place-items-center [color:var(--success)] shrink-0">
            <PackageCheck className="h-4 w-4" />
          </div>
          <h2 className="text-xl font-semibold tracking-tight">Record Goods Receipt</h2>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Recording receipt against {po.po_number} — {po.vendor.company_name}. Rejected items are
          recorded but do not count toward fulfilment.
        </p>
      </div>

      {formError && (
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {formError}
        </div>
      )}

      {/* Receipt details */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-sm font-semibold">Receipt Details</h3>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Receiver Name <span className="text-destructive">*</span>
            </label>
            <input
              type="text"
              value={receiverName}
              onChange={(e) => {
                setReceiverName(e.target.value);
                setHeaderError((prev) => ({ ...prev, receiver_name: undefined }));
              }}
              placeholder="Who received the delivery?"
              className={cn(
                "w-full rounded-lg border px-3 py-2 text-sm bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition",
                headerError.receiver_name ? "border-destructive" : "border-border",
              )}
            />
            {headerError.receiver_name && (
              <p className="text-xs text-destructive">{headerError.receiver_name}</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-medium">
              Date Received <span className="text-destructive">*</span>
            </label>
            <input
              type="date"
              value={dateReceived}
              max={todayStr()}
              onChange={(e) => {
                setDateReceived(e.target.value);
                setHeaderError((prev) => ({ ...prev, date_received: undefined }));
              }}
              className={cn(
                "w-full rounded-lg border px-3 py-2 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30 transition",
                headerError.date_received ? "border-destructive" : "border-border",
              )}
            />
            {headerError.date_received && (
              <p className="text-xs text-destructive">{headerError.date_received}</p>
            )}
          </div>

          <div className="md:col-span-2 space-y-1.5">
            <label className="text-sm font-medium">Delivery Reference</label>
            <input
              type="text"
              value={deliveryReference}
              onChange={(e) => setDeliveryReference(e.target.value)}
              placeholder="Carrier tracking number or delivery note (optional)"
              className="w-full rounded-lg border border-border px-3 py-2 text-sm bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition"
            />
          </div>
        </div>
      </div>

      {/* Received line items */}
      <div className="rounded-2xl bg-card border border-border shadow-elegant overflow-hidden">
        <div className="px-6 py-4 border-b border-border">
          <h3 className="text-sm font-semibold">Received Line Items</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            Enter the quantity received for each line. Leave a line at 0 if nothing was received for
            it.
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-xs text-muted-foreground">
              <tr>
                <th className="text-left font-medium px-5 py-3">Description</th>
                <th className="text-right font-medium px-3 py-3">Ordered</th>
                <th className="text-right font-medium px-3 py-3">Prev. Received</th>
                <th className="text-right font-medium px-3 py-3">Outstanding</th>
                <th className="text-left font-medium px-3 py-3 w-[8rem]">Qty Received *</th>
                <th className="text-left font-medium px-3 py-3 w-[9rem]">Condition</th>
                <th className="text-left font-medium px-5 py-3 w-[14rem]">Notes</th>
              </tr>
            </thead>
            <tbody>
              {sortedLines.map((li) => {
                const draft = lines[li.id];
                const errs = lineErrors[li.id] ?? {};
                const outstanding = Math.max(0, li.quantity - li.received_qty_total);
                const qty = parsedQty(li.id);
                // US-033: warn when this receipt would push cumulative Good
                // received past the ordered quantity. Only Good counts.
                const isOverReceipt =
                  draft.condition === "Good" && li.received_qty_total + qty > li.quantity;
                return (
                  <Fragment key={li.id}>
                    <tr className="border-t border-border align-top">
                      <td className="px-5 py-3 font-medium">{li.description}</td>
                      <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                        {li.quantity}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                        {li.received_qty_total}
                      </td>
                      <td className="px-3 py-3 text-right tabular-nums text-muted-foreground">
                        {outstanding}
                      </td>
                      <td className="px-3 py-3">
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={draft.quantity_received}
                          onChange={(e) => updateLine(li.id, "quantity_received", e.target.value)}
                          placeholder="0"
                          className={cn(
                            "w-full rounded border px-2 py-1.5 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-primary/30 transition tabular-nums",
                            errs.quantity_received ? "border-destructive" : "border-border",
                          )}
                        />
                        {errs.quantity_received && (
                          <p className="mt-1 text-xs text-destructive">{errs.quantity_received}</p>
                        )}
                      </td>
                      <td className="px-3 py-3">
                        <select
                          value={draft.condition}
                          onChange={(e) =>
                            updateLine(li.id, "condition", e.target.value as GRNCondition | "")
                          }
                          className={cn(
                            "w-full rounded border px-2 py-1.5 text-xs bg-background focus:outline-none focus:ring-1 focus:ring-primary/30 transition",
                            errs.condition ? "border-destructive" : "border-border",
                          )}
                        >
                          <option value="">Select…</option>
                          {CONDITIONS.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                        {errs.condition && (
                          <p className="mt-1 text-xs text-destructive">{errs.condition}</p>
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <input
                          type="text"
                          value={draft.notes}
                          onChange={(e) => updateLine(li.id, "notes", e.target.value)}
                          placeholder="Optional"
                          className="w-full rounded border border-border px-2 py-1.5 text-xs bg-background placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary/30 transition"
                        />
                      </td>
                    </tr>
                    {isOverReceipt && (
                      <tr className="bg-[var(--warning)]/5">
                        <td colSpan={7} className="px-5 pb-3">
                          <span className="inline-flex items-center gap-1.5 text-xs [color:var(--warning)]">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Over-receipt — cumulative Good received ({li.received_qty_total + qty})
                            exceeds the {li.quantity} ordered. This is allowed and will be recorded.
                          </span>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 pb-8">
        <Link
          to="/purchase-orders/$id"
          params={{ id: po.id }}
          className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
        >
          Cancel
        </Link>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saving && (
            <span className="h-4 w-4 rounded-full border-2 border-primary-foreground border-t-transparent animate-spin" />
          )}
          Record Receipt
        </button>
      </div>
    </div>
  );
}
